import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, Signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { select } from '@ngxs/store';
import {
    ApplicationAdditionalSubjectAreasComponent,
    ApplicationAttachmentsComponent,
    ApplicationDetailAffectedEmployeesComponent,
    ApplicationDetailApprovedBudgetComponent,
    ApplicationDetailBudgetComponent,
    ApplicationDetailDeadlinesComponent,
    ApplicationDetailEmployeeLearningHoursComponent,
    ApplicationDetailTextareaComponent,
    ApplicationInfluenceAndImpactComponent,
    ApplicationInformationComponent,
    ApplicationResponsibilitiesComponent,
    ApplicationStatusDetailComponent,
    BaseApplicationDetailComponent
} from '@tran-monorepo/frontend/components';
import { NotificationService } from '@tran-monorepo/frontend/services/notification.service';
import { PageCursorMetadata } from '@tran-monorepo/interfaces';
import { Application, ApplicationStatusEnum } from '@tran-monorepo/interfaces/applications';
import { File } from '@tran-monorepo/interfaces/files';
import { catchError, EMPTY, switchMap, take } from 'rxjs';
import { ContentSectionComponent } from '../../shared/components';
import {
    ClearApplicationAttachmentsState,
    DownloadApplicationAttachment,
    LoadApplicationAttachments,
    LoadMoreApplicationAttachments
} from '../../shared/states/application-attachments/application-attachments.actions';
import { ApplicationAttachmentsState } from '../../shared/states/application-attachments/application-attachments.state';
import {
    LoadApplication,
    LoadSelectedAwardCommitteeMeeting,
    ResetApplicationDetailState
} from './state/application-detail/application-detail.action';
import { CustomerApplicationDetailState } from './state/application-detail/application-detail.state';

@Component({
    selector: 'tran-application-detail',
    imports: [
        ContentSectionComponent,
        ApplicationInformationComponent,
        ApplicationStatusDetailComponent,
        ApplicationResponsibilitiesComponent,
        ApplicationInfluenceAndImpactComponent,
        ApplicationAdditionalSubjectAreasComponent,
        ApplicationDetailDeadlinesComponent,
        ApplicationAttachmentsComponent,
        ApplicationDetailBudgetComponent,
        ApplicationDetailTextareaComponent,
        ApplicationDetailAffectedEmployeesComponent,
        ApplicationDetailEmployeeLearningHoursComponent,
        TranslateModule,
        ApplicationDetailApprovedBudgetComponent
    ],
    templateUrl: './application-detail.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationDetailComponent extends BaseApplicationDetailComponent implements OnDestroy {
    private readonly notifications = inject(NotificationService);

    protected override readonly application: Signal<Application | null> = select(
        CustomerApplicationDetailState.application
    );
    protected override readonly attachments: Signal<File[]> = select(ApplicationAttachmentsState.attachments);
    protected override readonly isLoadingApplication: Signal<boolean> = select(
        CustomerApplicationDetailState.isLoadingApplication
    );
    protected readonly applicationKpis = select(CustomerApplicationDetailState.applicationKpis);
    protected override readonly attachmentsMetadata: Signal<PageCursorMetadata | null> = select(
        ApplicationAttachmentsState.attachmentsMetadata
    );
    protected readonly selectedAwardCommitteeMeeting = select(
        CustomerApplicationDetailState.selectedAwardCommitteeMeeting
    );
    protected readonly isSelectedAwardCommitteeMeetingLoading = select(
        CustomerApplicationDetailState.isSelectedAwardCommitteeMeetingLoading
    );

    protected readonly applicationStatusEnum = ApplicationStatusEnum;

    ngOnDestroy(): void {
        this.store.dispatch([new ClearApplicationAttachmentsState(), new ResetApplicationDetailState()]);
    }

    protected override loadData(applicationId: string): void {
        this.store
            .dispatch(new LoadApplication(applicationId))
            .pipe(
                switchMap(() => this.store.dispatch(new LoadSelectedAwardCommitteeMeeting())),
                switchMap(() => this.store.dispatch(new LoadApplicationAttachments(applicationId))),
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 401) {
                        this.notifications.notify('danger', 'application-detail.notifications.load.unauthorized');
                        return EMPTY;
                    }

                    if (error.status === 403) {
                        this.notifications.notify('danger', 'application-detail.notifications.load.forbidden');
                        return EMPTY;
                    }

                    if (error.status === 404) {
                        this.notifications.notify('danger', 'application-detail.notifications.load.not-found');
                        return EMPTY;
                    }

                    if (error.status >= 500) {
                        this.notifications.notify('danger', 'application-detail.notifications.load.server-error');
                        return EMPTY;
                    }

                    this.notifications.notify('danger', 'application-detail.notifications.load.error');
                    return EMPTY;
                }),
                take(1)
            )
            .subscribe();
    }

    protected override dispatchDownloadAttachment(file: File): void {
        this.store.dispatch(
            new DownloadApplicationAttachment({ applicationId: this.application()?.id ?? '', fileId: file.id })
        );
    }

    protected override dispatchLoadMoreAttachments(): void {
        this.store.dispatch(new LoadMoreApplicationAttachments());
    }
}
